import { useEffect, useRef, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Globe, Search, RefreshCw, Loader2, CheckCircle2, Trash2, Copy } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

const POLL_INTERVAL_MS = 15000;

const STATUS_LABELS = {
  draft: "Draft",
  records_pending: "Waiting for DNS records",
  records_verified: "Records verified — issuing certificate",
  cloudflare_hostname_provisioning: "Provisioning…",
  cloudflare_cert_issued: "Certificate issued — connecting app",
  azure_binding_pending: "Connecting app…",
  live: "Live",
  error: "Error",
  stalled: "Stalled — needs attention",
  renewal_degraded: "Renewal at risk",
};

const IN_PROGRESS_STATUSES = new Set([
  "records_pending", "records_verified", "cloudflare_hostname_provisioning",
  "cloudflare_cert_issued", "azure_binding_pending",
]);

const PURPOSE_LABELS = {
  routing: "Routing",
  pre_validation: "Hostname pre-validation",
  dcv_delegation: "Certificate validation (auto-renewing)",
};

function statusBadgeVariant(status) {
  if (status === "live") return "default";
  if (status === "error" || status === "stalled" || status === "renewal_degraded") return "destructive";
  return "secondary";
}

export default function DnsSetup() {
  const { user } = useAuth();
  // dealer.dns.manage is MHQ-only now, so anyone who can reach this page
  // can run the wizard for any dealer — there is no dealer-side variant.
  const canManageAll = hasGlobalPermission(user, PERM.DNS_MANAGE);
  const [dealer, setDealer] = useState(canManageAll ? null : { id: user.dealer_id });

  return (
    <div className="mx-auto max-w-3xl p-6 md:p-10" data-testid="dns-setup-page">
      <div className="mb-8">
        <p className="data-label mb-2">DEALER WEBSITE</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">DNS Setup</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Connect your dealer website&apos;s domain so it resolves and serves over HTTPS.
        </p>
      </div>

      {canManageAll && dealer && (
        <button
          type="button"
          className="mb-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          onClick={() => setDealer(null)}
          data-testid="dns-change-dealer"
        >
          ← Choose a different dealer
        </button>
      )}

      {canManageAll && !dealer ? (
        <DealerPicker onPick={setDealer} />
      ) : (
        <DealerDnsPanel dealerId={dealer.id} dealerName={dealer.name} />
      )}
    </div>
  );
}

function DealerPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await client.get("/dealers", { params: q ? { q, limit: 20 } : { limit: 20 } });
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Card className="p-6" data-testid="dns-dealer-picker">
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="dns-dealer-search"
          placeholder="Search dealer name / code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
          autoFocus
        />
      </div>
      <div className="max-h-96 overflow-auto rounded-md border border-border">
        {items.map((d) => (
          <button
            key={d.id}
            type="button"
            data-testid={`dns-dealer-row-${d.id}`}
            onClick={() => onPick({ id: d.id, name: d.name })}
            className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted"
          >
            <div>
              <div className="text-sm font-medium">{d.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{d.global_id} · {d.company_id}</div>
            </div>
          </button>
        ))}
        {!loading && items.length === 0 && (
          <p className="p-4 text-center text-xs text-muted-foreground">No dealers found.</p>
        )}
      </div>
    </Card>
  );
}

function DealerDnsPanel({ dealerId, dealerName }) {
  const [site, setSite] = useState(undefined); // undefined = loading, null = none started yet
  const [validating, setValidating] = useState(false);
  const intervalRef = useRef(null);

  const load = () => client.get(`/dns/sites/${dealerId}`).then((r) => setSite(r.data || null));

  useEffect(() => {
    setSite(undefined);
    load();
    return () => clearInterval(intervalRef.current);
  }, [dealerId]); // eslint-disable-line

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (site && IN_PROGRESS_STATUSES.has(site.status)) {
      intervalRef.current = setInterval(load, POLL_INTERVAL_MS);
    }
    return () => clearInterval(intervalRef.current);
  }, [site?.status, dealerId]); // eslint-disable-line

  const validate = async () => {
    setValidating(true);
    try {
      const { data } = await client.post(`/dns/sites/${site.id}/validate`);
      setSite(data);
      toast.success("Checked — see updated status below");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this DNS setup and start over?")) return;
    try {
      await client.delete(`/dns/sites/${site.id}`);
      setSite(null);
      toast.success("DNS setup deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  if (site === undefined) return <Skeleton className="h-64" />;
  if (site === null) return <NewSiteForm dealerId={dealerId} onCreated={setSite} />;

  return (
    <div data-testid="dns-site-panel">
      <Card className="mb-6 p-6" data-testid="dns-site-status-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            {dealerName && <p className="text-xs text-muted-foreground">{dealerName}</p>}
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Globe className="h-4 w-4" /> {site.fqdn}
            </h2>
          </div>
          <Badge variant={statusBadgeVariant(site.status)} data-testid="dns-site-status-badge">
            {STATUS_LABELS[site.status] || site.status}
          </Badge>
        </div>
        {site.error_message && (
          <p
            className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive"
            data-testid="dns-site-error"
          >
            {site.error_message}
          </p>
        )}
        <div className="flex gap-2">
          <Button data-testid="dns-validate-btn" variant="outline" className="gap-1.5" onClick={validate} disabled={validating}>
            {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Validate
          </Button>
          <Button
            data-testid="dns-delete-btn"
            variant="outline"
            className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={remove}
          >
            <Trash2 className="h-4 w-4" /> Start over
          </Button>
        </div>
      </Card>

      {site.domain_type === "independent" && site.records?.length > 0 && <RecordsCard records={site.records} />}

      {site.status === "live" && (
        <Card className="border-emerald-500/40 bg-emerald-500/5 p-4 text-sm" data-testid="dns-site-live">
          <p className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" /> This site is live.
          </p>
        </Card>
      )}
    </div>
  );
}

function RecordsCard({ records }) {
  const copy = (v) => {
    navigator.clipboard?.writeText(v);
    toast.success("Copied");
  };

  return (
    <Card className="mb-6 p-6" data-testid="dns-records-card">
      <h2 className="mb-1 text-lg font-semibold">Add these records to your DNS</h2>
      <p className="mb-4 text-xs text-muted-foreground">
        Add all {records.length} records at your DNS provider, then click Validate above. We also recheck
        automatically every few minutes until they&apos;re all in place.
      </p>
      <div className="space-y-4">
        {records.map((r, i) => (
          <div key={i} className="rounded-md border border-border p-3" data-testid={`dns-record-${r.purpose}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {PURPOSE_LABELS[r.purpose] || r.purpose} · {r.type}
              </span>
              {r.verified ? (
                <Badge className="gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Verified
                </Badge>
              ) : (
                <Badge variant="secondary">Pending</Badge>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div>
                <Label className="data-label mb-1 block">Name</Label>
                <div className="flex gap-1.5">
                  <Input readOnly value={r.host} className="font-mono text-xs" />
                  <Button variant="outline" size="icon" onClick={() => copy(r.host)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="data-label mb-1 block">Value</Label>
                <div className="flex gap-1.5">
                  <Input readOnly value={r.expected_value} className="font-mono text-xs" />
                  <Button variant="outline" size="icon" onClick={() => copy(r.expected_value)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NewSiteForm({ dealerId, onCreated }) {
  const [domainType, setDomainType] = useState("independent");
  const [fqdn, setFqdn] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      const body = { dealer_id: dealerId, domain_type: domainType };
      if (domainType === "independent") body.fqdn = fqdn.trim();
      else body.subdomain_slug = slug.trim();
      const { data } = await client.post("/dns/sites", body);
      onCreated(data);
      toast.success("DNS setup started");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not start DNS setup");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6" data-testid="dns-new-site-form">
      <h2 className="mb-4 text-lg font-semibold">Choose your domain type</h2>
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          data-testid="dns-domain-type-independent"
          onClick={() => setDomainType("independent")}
          className={`flex-1 rounded-md border px-4 py-3 text-left text-sm transition ${
            domainType === "independent" ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
          }`}
        >
          <div className="font-medium">My own domain</div>
          <div className="text-xs text-muted-foreground">e.g. www.mydealer.com</div>
        </button>
        <button
          type="button"
          data-testid="dns-domain-type-shared"
          onClick={() => setDomainType("shared_subdomain")}
          className={`flex-1 rounded-md border px-4 py-3 text-left text-sm transition ${
            domainType === "shared_subdomain" ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
          }`}
        >
          <div className="font-medium">A subdomain of the market site</div>
          <div className="text-xs text-muted-foreground">e.g. mydealer.skodaauto.de</div>
        </button>
      </div>

      {domainType === "independent" ? (
        <div className="mb-4">
          <Label className="data-label mb-1 block">Your domain</Label>
          <Input
            data-testid="dns-fqdn-input"
            placeholder="www.mydealer.com"
            value={fqdn}
            onChange={(e) => setFqdn(e.target.value)}
            className="font-mono text-xs"
          />
        </div>
      ) : (
        <div className="mb-4">
          <Label className="data-label mb-1 block">Subdomain</Label>
          <Input
            data-testid="dns-slug-input"
            placeholder="mydealer"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="font-mono text-xs"
          />
        </div>
      )}

      <Button
        data-testid="dns-start-btn"
        onClick={create}
        disabled={saving || (domainType === "independent" ? !fqdn.trim() : !slug.trim())}
        className="gap-1.5"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        Start setup
      </Button>
    </Card>
  );
}
